// Squarified treemap layout.
//
// Pure geometry: values in, rectangles out, no DOM. Kept separate from the
// renderer because the algorithm is the part that can be wrong in ways a
// screenshot will not reveal — a rectangle whose area misrepresents its value
// is a chart that lies quietly.
//
// The algorithm is Bruls, Huizing and van Wijk (2000): lay tiles into a strip
// along the shorter edge, extending the strip while doing so improves the worst
// aspect ratio in it, then start a new strip. Naive slice-and-dice produces
// correct areas but slivers too thin to label.

/** Worst aspect ratio in a row, given the row's total and the strip length. */
function worstRatio(row, length, total) {
    if (row.length === 0 || total <= 0 || length <= 0) return Infinity;
    const max = Math.max(...row);
    const min = Math.min(...row);
    const side = length * length;
    const sum = total * total;
    return Math.max((side * max) / sum, sum / (side * min));
}

/**
 * @param {Array<{value: number}>} items  Sorted or not; sorted descending here.
 * @returns {Array<{item, x, y, width, height}>} in the input's own order.
 */
export function squarify(items, width, height) {
    if (!items?.length || width <= 0 || height <= 0) return [];

    // Zero and negative values have no area and would divide by zero below. A
    // short position is a real thing to hold but not a thing with an area.
    const usable = items
        .map((item, index) => ({ item, index, value: Number(item.value) || 0 }))
        .filter((entry) => entry.value > 0)
        .sort((a, b) => b.value - a.value);

    if (usable.length === 0) return [];

    const total = usable.reduce((acc, entry) => acc + entry.value, 0);
    const scale = (width * height) / total;
    const scaled = usable.map((entry) => ({ ...entry, area: entry.value * scale }));

    const out = [];
    let x = 0;
    let y = 0;
    let w = width;
    let h = height;
    let index = 0;

    while (index < scaled.length) {
        const vertical = w >= h;
        const length = vertical ? h : w;

        // Grow the row while the worst aspect ratio keeps improving.
        const row = [];
        let rowTotal = 0;
        while (index + row.length < scaled.length) {
            const next = scaled[index + row.length].area;
            const current = worstRatio(row.map((r) => r.area), length, rowTotal);
            const candidate = worstRatio(
                [...row.map((r) => r.area), next],
                length,
                rowTotal + next,
            );
            if (row.length > 0 && candidate > current) break;
            row.push(scaled[index + row.length]);
            rowTotal += next;
        }

        // Lay the row out across the strip, then shrink the remaining area.
        const thickness = rowTotal / length;
        let offset = 0;
        for (const entry of row) {
            const extent = entry.area / thickness;
            out.push({
                item: entry.item,
                index: entry.index,
                x: vertical ? x : x + offset,
                y: vertical ? y + offset : y,
                width: vertical ? thickness : extent,
                height: vertical ? extent : thickness,
            });
            offset += extent;
        }

        if (vertical) {
            x += thickness;
            w -= thickness;
        } else {
            y += thickness;
            h -= thickness;
        }
        index += row.length;
    }

    return out.sort((a, b) => a.index - b.index);
}

/**
 * Colour for a percentage gain or loss, in discrete steps.
 *
 * Stepped rather than continuous so two tiles can actually be compared: on a
 * smooth gradient the eye cannot tell +9% from +13%, but it can tell one bucket
 * from the next. It is also why the legend can be drawn at all.
 *
 * The stops are fixed, not derived from the best and worst holding. A portfolio
 * where everything is up 1% should look uniformly, faintly green rather than
 * spanning the full palette, and "dark green" has to mean the same thing next
 * month as it does today.
 *
 * The bands are wider than a daily-change map's ±3% because this shows
 * unrealised P/L over a holding period, which ranges much further.
 */
export const PNL_STOPS = [3, 8, 15, 25];

const GREENS = ['#1a3d24', '#1f6b35', '#268c43', '#2fae53', '#3ccb62'];
const REDS = ['#4a1f1f', '#7d2626', '#a32d2d', '#c73838', '#e04747'];

/** Index into the 5-step ramp for a magnitude. */
function step(magnitude) {
    for (let i = 0; i < PNL_STOPS.length; i++) {
        if (magnitude < PNL_STOPS[i]) return i;
    }
    return PNL_STOPS.length;
}

export function pnlColor(percent) {
    if (percent === null || percent === undefined || !Number.isFinite(percent)) {
        return { background: '#1a1a1a', text: '#8a8a8a' };
    }
    const index = step(Math.abs(percent));
    return percent >= 0
        ? { background: GREENS[index], text: index >= 3 ? '#03210b' : '#dcffe4' }
        : { background: REDS[index], text: index >= 3 ? '#2a0505' : '#ffe2e2' };
}

/**
 * The legend's swatches, from most negative to most positive.
 *
 * Each swatch is labelled with the LOWER bound of the band it represents —
 * band `i` covers [STOPS[i-1], STOPS[i]) — so a tile's colour can be read off
 * the scale directly. The outermost band is open-ended and says so.
 */
export function pnlLegend() {
    const last = PNL_STOPS.length;
    const bound = (i) => (i === 0 ? '0' : String(PNL_STOPS[i - 1]));
    const open = (i) => (i === last ? '+' : '');

    const out = [];
    for (let i = last; i >= 0; i--) {
        out.push({ label: `−${bound(i)}%${open(i)}`, background: REDS[i] });
    }
    for (let i = 0; i <= last; i++) {
        out.push({ label: `+${bound(i)}%${open(i)}`, background: GREENS[i] });
    }
    return out;
}
